import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import TextField from "@mui/material/TextField";
import {
  Button,
  DialogActions,
  FormControl,
  FormHelperText,
  Typography,
} from "@material-ui/core";
import InfoIcon from "@mui/icons-material/Info";
import { trim } from "lodash";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../../common/components/Helper/Utils";
import { voidPayment } from "./../../dashboard/actions/dashboardAction";
import amountFormatter from "../../common/components/amountFormat";

const isQboAccountVerified = () =>
  window.sessionStorage.getItem("isQboVerified") === "true";

/** Modal tip only when account is QBO-verified AND invoice/payment is QBO-linked. */
const isQboSyncedPayment = (invoice, payment) => {
  if (!isQboAccountVerified()) return false;
  return (
    invoice?.is_invoice_qbo_synced === true ||
    payment?.is_qbo_synced === true ||
    payment?.is_invoice_qbo_synced === true ||
    payment?.qbo_synced === true ||
    payment?.isQboLinked === true
  );
};

export default function VoidPaymentModal(props) {
  const {
    open,
    handleClose,
    invoice,
    payment,
    onVoidComplete,
    isMultiplePayment,
    voidScope,
  } = props;
  const [reason, setReason] = useState("");
  const [errorText, setErrorText] = useState("");
  const [disabled, setDisabled] = useState(false);

  const showQboNote = isQboSyncedPayment(invoice, payment);

  useEffect(() => {
    if (open) {
      setReason("");
      setErrorText("");
      setDisabled(false);
    }
  }, [open]);

  const paymentId = payment?.id ?? payment?.paymentid ?? payment?.paymentId;
  const paymentAmount = payment?.amount;
  const paymentMethod = payment?.paymentmethod || "-";
  const invoiceIdList = (payment?.multipleinvoices || "")
   .toString()
   .split(",")
   .map((id) => id.trim())
   .filter(Boolean);
  const isMultipleInvoicePayment = invoiceIdList.length > 1;

  const singleInvoiceId = payment?.invoiceid ?? invoice?.id ?? "-";
  const displayInvoiceId =
    voidScope === "single"
      ? singleInvoiceId
      : voidScope === "all"
      ? invoiceIdList.length > 0
        ? invoiceIdList.join(", ")
        : singleInvoiceId
      : isMultipleInvoicePayment
      ? payment?.multipleinvoices
      : singleInvoiceId;
  const submitVoidPayment = () => {
    if (reason.length >= 1000) {
      setErrorText("Reason cannot exceed 1000 characters.");
      return;
    }
    if (trim(reason) === "") {
      setErrorText("Void reason should not be blank.");
      return;
    }
    if (!paymentId) {
      showErrorToast("Payment ID is missing. Cannot void this payment.");
      return;
    }

    setDisabled(true);
    const data = {
      paymentId,
      invoiceId: invoice?.id,
      accountId: invoice?.accountid || window.sessionStorage.getItem("accountId"),
      userId: invoice?.userid,
      reason: trim(reason),
      amount: paymentAmount,
      paymentMethod,
    };
    if (voidScope === "all") {
      data.ismultiplepaymnet = true;
    }

    const isAlreadyVoidedMessage = (message) =>
      (message || "").toString().toLowerCase().includes("already voided");

    voidPayment(data, (err, results) => {
      setDisabled(false);
      if (err) {
        const message =
          err?.response?.data?.message || err?.message || "Failed to void payment";
        if (isAlreadyVoidedMessage(message)) {
          showSuccessToast("Payment is already voided");
          handleClose();
          onVoidComplete?.(err?.response?.data || { alreadyVoided: true });
          return;
        }
        showErrorToast(message);
        return;
      }
      if (results?.success === 0 || results?.status === 0) {
        if (isAlreadyVoidedMessage(results?.message)) {
          showSuccessToast("Payment is already voided");
          handleClose();
          onVoidComplete?.({ ...results, alreadyVoided: true });
          return;
        }
        showErrorToast(results?.message || "Failed to void payment");
        return;
      }

      // status: 1 — soft-void succeeded in WatchOps
      const payload = results?.data || {};
      const message =
        results?.message || "Payment voided successfully.";
      const qboManualVoidRequired =
        results?.qboManualVoidRequired === true ||
        payload?.qboManualVoidRequired === true;
      const qboWarning = payload?.qboWarning || results?.qboWarning || message;

      if (qboManualVoidRequired) {
        showSuccessToast("Payment voided successfully.");
        showWarningToast(qboWarning);
      } else {
        showSuccessToast(message);
      }

      handleClose();
      onVoidComplete?.(results);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="void-payment-dialog-title"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="void-payment-dialog-title">
        Void Payment
        <IconButton
          onClick={handleClose}
          style={{ float: "right" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <div style={{ fontSize: "18px", lineHeight: 1.6, marginBottom: 8 }}>
          Are you sure you want to <strong>Void</strong> this payment?
        </div>
        <Typography variant="body2" style={{ marginBottom: 4 }}>
          Invoice ID: <strong>{displayInvoiceId}</strong>
        </Typography>
        <Typography variant="body2" style={{ marginBottom: 4 }}>
          Amount:{" "}
          <strong>
            $
            {paymentAmount !== null && paymentAmount !== undefined
              ? amountFormatter(Number(paymentAmount))
              : "-"}
          </strong>
        </Typography>
        <Typography variant="body2" style={{ marginBottom: 16 }}>
          Payment Method: <strong>{paymentMethod}</strong>
        </Typography>

        <FormControl style={{ width: "100%", marginBottom: 16 }}>
          <TextField
            id="void-payment-reason"
            label="Please enter the reason for void"
            multiline
            maxRows={4}
            required
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setErrorText("");
            }}
          />
          <FormHelperText className="error-o">{errorText}</FormHelperText>
        </FormControl>

        <p
          style={{
            display: "flex",
            alignItems: "flex-start",
            fontSize: 14,
            fontWeight: 500,
            lineHeight: 1.5,
            margin: 0,
            marginBottom: showQboNote ? 12 : 0,
          }}
        >
          <InfoIcon
            style={{
              marginRight: 8,
              marginTop: 2,
              fontSize: 20,
              color: "rgba(0, 0, 0, 0.6)",
              flexShrink: 0,
            }}
          />
          Invoice stays Active. Balance and payment status will be recalculated.
        </p>

        {showQboNote && (
          <p
            style={{
              display: "flex",
              alignItems: "flex-start",
              fontSize: 14,
              fontWeight: 500,
              lineHeight: 1.5,
              margin: 0,
              color: "#b26a00",
            }}
          >
            <InfoIcon
              style={{
                marginRight: 8,
                marginTop: 2,
                fontSize: 20,
                color: "#b26a00",
                flexShrink: 0,
              }}
            />
            This void payment will not be reflected in QuickBooks. Please void this
            payment manually in QuickBooks.
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" autoFocus onClick={handleClose}>
          Cancel
        </Button>
        <Button color="primary" disabled={disabled} onClick={submitVoidPayment}>
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
}
